import React, { useState } from 'react';
import { Position } from './PositionCard';

interface Props {
  position: Position;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

function fmtPrice(n: number | null) {
  if (n === null) return '—';
  if (n >= 1000) return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (n >= 1) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(6)}`;
}

function platformLabel(exchange: string) {
  if (exchange === 'HYPERLIQUID') return 'Hyperliquid';
  if (exchange === 'BINANCE') return 'Binance';
  return 'Bitget';
}

function platformClass(exchange: string) {
  if (exchange === 'HYPERLIQUID') return 'hl';
  if (exchange === 'BINANCE') return 'bn';
  return 'bg';
}

const ClosePositionModal: React.FC<Props> = ({ position: pos, onConfirm, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const pnl = pos.unrealized_pnl;
  const pnlPct = pos.unrealized_pnl_pct;
  const pnlClass = pnl === null ? '' : pnl >= 0 ? 'green' : 'red';
  const pnlSign = pnl !== null && pnl >= 0 ? '+' : '';
  const pnlPctSign = pnlPct !== null && pnlPct >= 0 ? '+' : '';

  const handleConfirm = async () => {
    setLoading(true);
    setError('');
    try {
      await onConfirm();
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || '平仓失败');
      setLoading(false);
    }
  };

  return (
    <div className="pos-overlay" onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div className="pos-modal">
        <div className="pos-modal-head">
          <div className="pos-modal-title">
            <span className={`pos-platform ${platformClass(pos.exchange)}`}>
              <span className="pos-platform-dot" />
              {platformLabel(pos.exchange)}
            </span>
            Close Position
          </div>
          <button className="pos-modal-x" onClick={onCancel}>×</button>
        </div>

        <div className="pos-modal-body">
          <div className="pos-warn-banner">
            ⚠️ This will execute a market order on {platformLabel(pos.exchange)} immediately. This action cannot be undone.
          </div>

          {error && <div className="pos-error">{error}</div>}

          <div className="pos-sum-row">
            <span className="pos-sum-label">Position</span>
            <span className="pos-sum-val">
              {pos.symbol}-PERP · <span className={pos.direction === 'LONG' ? 'green' : 'red'} style={{ color: pos.direction === 'LONG' ? '#16a34a' : '#dc2626' }}>{pos.direction}</span>
            </span>
          </div>
          <div className="pos-sum-row">
            <span className="pos-sum-label">Size</span>
            <span className="pos-sum-val">{pos.remaining_quantity} {pos.symbol}</span>
          </div>
          <div className="pos-sum-row">
            <span className="pos-sum-label">Entry Price</span>
            <span className="pos-sum-val">{fmtPrice(pos.entry_price)}</span>
          </div>
          <div className="pos-sum-row">
            <span className="pos-sum-label">Est. Exit Price</span>
            <span className="pos-sum-val">{pos.current_price ? `~${fmtPrice(pos.current_price)} (market)` : '—'}</span>
          </div>
          <div className="pos-sum-row">
            <span className="pos-sum-label">Est. Realized PnL</span>
            <span className={`pos-sum-val ${pnlClass}`}>
              {pnl === null ? '—' : `${pnlSign}$${Math.abs(pnl).toFixed(2)} (${pnlPctSign}${pnlPct?.toFixed(1)}%)`}
            </span>
          </div>
        </div>

        <div className="pos-modal-foot">
          <button className="pos-btn pos-btn-outline" onClick={onCancel} disabled={loading}>Cancel</button>
          <button className="pos-btn pos-btn-danger" onClick={handleConfirm} disabled={loading}>
            {loading ? <><span className="pos-spinner" />Closing...</> : 'Confirm Close at Market'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClosePositionModal;
